'use client'

import { useState, useRef, useEffect } from 'react';
import { X, ArrowLeft, PaperPlaneTilt } from 'phosphor-react';
import { useAssistant } from '@/lib/assistantContext';
import MessageBubble from './assistant/MessageBubble';
import QuickReplies from './assistant/QuickReplies';
import TypingIndicator from './assistant/TypingIndicator';
import { streamChat, type Message } from '@/lib/streamChat';

const INITIAL_MESSAGE: Message = {
  role: 'assistant',
  content: "Hi! I help businesses connect with Chicago's LGBTQ+ community through Windy City Media Group. We're celebrating 40 years of trusted journalism in 2025!\n\nWhat brings you here today?"
};

const INITIAL_QUICK_REPLIES = [
  "Promote an event",
  "Reach LGBTQ+ customers",
  "Build brand visibility",
  "Event sponsorship",
  "Just exploring"
];

export default function AssistantPanel() {
  const { isOpen, closePanel } = useAssistant();
  const [messages, setMessages] = useState<Message[]>([INITIAL_MESSAGE]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [quickReplies, setQuickReplies] = useState<string[]>(INITIAL_QUICK_REPLIES);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Lock body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleStartOver = () => {
    setMessages([INITIAL_MESSAGE]);
    setQuickReplies(INITIAL_QUICK_REPLIES);
    setInputValue('');
    setIsTyping(false);
  };

  const handleSend = async (text: string) => {
    if (!text.trim() || isTyping) return;

    const userMessage: Message = { role: 'user', content: text.trim() };
    const updatedMessages = [...messages, userMessage];
    
    setMessages(updatedMessages);
    setInputValue('');
    setQuickReplies([]);
    setIsTyping(true);

    let assistantContent = '';

    await streamChat({
      messages: updatedMessages.slice(1), // Skip initial greeting for API
      onDelta: (chunk) => {
        assistantContent += chunk;
        setMessages(prev => {
          const last = prev[prev.length - 1];
          if (last?.role === 'assistant' && prev.length > updatedMessages.length) {
            return prev.map((m, i) => 
              i === prev.length - 1 ? { ...m, content: assistantContent } : m
            );
          }
          return [...prev.slice(0, -1), userMessage, { role: 'assistant', content: assistantContent }];
        });
      },
      onDone: () => {
        setIsTyping(false);
      },
      onError: (error) => {
        setMessages(prev => [
          ...prev,
          { role: 'assistant', content: `I'm sorry, I encountered an issue: ${error}. Please try again or contact us directly at terri@windycitymediagroup.com` }
        ]);
        setIsTyping(false);
      },
    });
  };

  const handleQuickReply = (option: string) => {
    handleSend(option);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSend(inputValue);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[60] sm:bg-black/30"
        onClick={closePanel}
      />

      {/* Panel */}
      <div
        className="fixed top-0 right-0 h-full w-full sm:max-w-[450px] bg-[#fdfcfa] z-[70] 
          flex flex-col animate-panel-slide-in shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 h-14 border-b border-[#e5e5e5] flex-shrink-0">
          <button
            onClick={closePanel}
            className="sm:hidden p-2 -ml-2 hover:bg-[#f0f0f0] rounded-full transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft size={24} color="#103065" />
          </button>
          <div className="flex flex-col items-center sm:items-start">
            <h2 className="text-base font-semibold text-[#103065] font-primary">
              Assistant
            </h2>
            <span className="text-xs text-[#888] font-normal">
              Windy City Times
            </span>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              onClick={handleStartOver}
              className="text-xs text-[#888] hover:text-[#103065] transition-colors px-2 py-1"
            >
              Start over
            </button>
            <button
              onClick={closePanel}
              className="p-2 -mr-2 hover:bg-[#f0f0f0] rounded-full transition-colors"
              aria-label="Close panel"
            >
              <X size={24} color="#103065" />
            </button>
          </div>
          <button
            onClick={handleStartOver}
            className="sm:hidden text-xs text-[#888] hover:text-[#103065] transition-colors px-2 py-1"
          >
            Start over
          </button>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {messages.map((msg, index) => (
            <MessageBubble key={index} role={msg.role} content={msg.content} />
          ))}
          
          {isTyping && <TypingIndicator />}
          
          {!isTyping && quickReplies.length > 0 && (
            <QuickReplies
              options={quickReplies}
              onSelect={handleQuickReply}
              disabled={isTyping}
            />
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <form
          onSubmit={handleSubmit}
          className="flex items-center gap-3 px-4 py-3 border-t border-[#e5e5e5] bg-[#fdfcfa] flex-shrink-0"
        >
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Type a message..."
            disabled={isTyping}
            className="flex-1 px-4 py-2.5 text-sm bg-white border border-[#e5e5e5] rounded-full 
              focus:outline-none focus:border-[#103065] focus:ring-1 focus:ring-[#103065]
              disabled:opacity-50 disabled:cursor-not-allowed text-[#1E1E1E] placeholder:text-[#888]"
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || isTyping}
            className="w-10 h-10 flex items-center justify-center bg-[#F5CC5C] rounded-full 
              transition-all duration-200 hover:bg-[#e6bd4d] disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Send message"
          >
            <PaperPlaneTilt size={20} color="#103065" weight="fill" />
          </button>
        </form>
      </div>
    </>
  );
}
